/**
 * Task write tools — create, update and delete checklist tasks by reusing the
 * existing Firestore helpers in features/tasks/operations.ts.
 */

import { Schema } from "firebase/ai";
import { TaskSchema } from "@amazing-wedding/shared-types";
import type { TaskDraft, TaskPatch } from "../operations";
import type { PlannerTool } from "./registry";
import { asEnum, asString, normalizeDate, requireString } from "./helpers";

/** Cap on items accepted by a single batch call — keeps one turn bounded. */
const MAX_BATCH = 50;

const STATUS_VALUES = ["todo", "inProgress", "done"] as const;
const PRIORITY_VALUES = ["low", "medium", "high"] as const;
const CATEGORY_VALUES = [
  "venue",
  "catering",
  "photography",
  "music",
  "flowers",
  "attire",
  "transport",
  "beauty",
  "invitations",
  "ceremony",
  "honeymoon",
  "other",
] as const;

const TaskDraftSchema = TaskSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  completedAt: true,
});

function taskTitle(ctx: { tasks: () => { id: string; title: string }[] }, id: string): string {
  return ctx.tasks().find((t) => t.id === id)?.title ?? id;
}

/** Build (and validate) a TaskDraft from loosely-typed function-call args. */
function buildTaskDraft(args: Record<string, unknown>): { draft: TaskDraft; parsed: TaskDraft } {
  const category = asEnum(args.category, CATEGORY_VALUES);
  if (!category) throw new Error("A valid 'category' is required.");
  const draft: TaskDraft = {
    title: requireString(args, "title"),
    description: asString(args.description) ?? null,
    dueDate: normalizeDate(args.dueDate) ?? null,
    priority: asEnum(args.priority, PRIORITY_VALUES) ?? "medium",
    status: asEnum(args.status, STATUS_VALUES) ?? "todo",
    category,
    assigneeId: null,
  };
  return { draft, parsed: TaskDraftSchema.parse(draft) };
}

/** Shared schema for one task in single- and batch-create tools. */
const TASK_INPUT_PROPERTIES = {
  title: Schema.string({ description: "Short task title." }),
  category: Schema.enumString({
    enum: [...CATEGORY_VALUES],
    description: "Task category. Pick the closest match; use 'other' if unsure.",
  }),
  description: Schema.string({ description: "Longer details (optional)." }),
  dueDate: Schema.string({ description: "Due date in ISO-8601 (optional)." }),
  priority: Schema.enumString({
    enum: [...PRIORITY_VALUES],
    description: "Priority. Defaults to 'medium'.",
  }),
  status: Schema.enumString({
    enum: [...STATUS_VALUES],
    description: "Status. Defaults to 'todo'.",
  }),
};
const TASK_INPUT_OPTIONAL = ["description", "dueDate", "priority", "status"];

export const taskTools: PlannerTool[] = [
  {
    declaration: {
      name: "create_task",
      description:
        "Add a new task to the wedding checklist. Use when the user wants to plan, remember or schedule something.",
      parameters: Schema.object({
        properties: { ...TASK_INPUT_PROPERTIES },
        optionalProperties: [...TASK_INPUT_OPTIONAL],
      }),
    },
    destructive: false,
    describe: (args) => `Dodanie zadania: ${asString(args.title) ?? "?"}`,
    execute: async (args, ctx) => {
      const { draft, parsed } = buildTaskDraft(args);
      const id = await ctx.operations.tasks.createTask(ctx.weddingId, parsed);
      return { ok: true, taskId: id, title: draft.title };
    },
  },
  {
    declaration: {
      name: "create_tasks_batch",
      description:
        "Add several tasks to the checklist in a single call. STRONGLY PREFER this over multiple create_task calls when the user asks for more than one task (e.g. a generated checklist) — it avoids exhausting the per-message tool-call budget. Each item follows the same shape as create_task.",
      parameters: Schema.object({
        properties: {
          tasks: Schema.array({
            description: `List of tasks to create (max ${MAX_BATCH}).`,
            items: Schema.object({
              properties: { ...TASK_INPUT_PROPERTIES },
              optionalProperties: [...TASK_INPUT_OPTIONAL],
            }),
          }),
        },
      }),
    },
    destructive: false,
    describe: (args) => {
      const list = Array.isArray(args.tasks) ? args.tasks : [];
      return `Dodanie zadań: ${list.length}`;
    },
    execute: async (args, ctx) => {
      const rawList = Array.isArray(args.tasks) ? (args.tasks as Record<string, unknown>[]) : [];
      if (rawList.length === 0) throw new Error("The 'tasks' array is empty.");
      if (rawList.length > MAX_BATCH) {
        throw new Error(`Too many tasks (${rawList.length}). Send at most ${MAX_BATCH} per call.`);
      }
      const created: { taskId: string; title: string }[] = [];
      const failed: { index: number; title: string | null; error: string }[] = [];
      for (let i = 0; i < rawList.length; i++) {
        const item = rawList[i] ?? {};
        try {
          const { draft, parsed } = buildTaskDraft(item);
          const id = await ctx.operations.tasks.createTask(ctx.weddingId, parsed);
          created.push({ taskId: id, title: draft.title });
        } catch (err) {
          failed.push({
            index: i,
            title: asString(item.title) ?? null,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
      return { ok: failed.length === 0, createdCount: created.length, created, failed };
    },
  },
  {
    declaration: {
      name: "update_task",
      description:
        "Update an existing task. You MUST first call list_tasks to obtain the exact taskId — never invent an ID. Only the fields you pass are changed.",
      parameters: Schema.object({
        properties: {
          taskId: Schema.string({ description: "Exact task ID from list_tasks." }),
          title: Schema.string(),
          description: Schema.string(),
          dueDate: Schema.string({ description: "Due date in ISO-8601." }),
          priority: Schema.enumString({ enum: [...PRIORITY_VALUES] }),
          status: Schema.enumString({ enum: [...STATUS_VALUES] }),
          category: Schema.enumString({ enum: [...CATEGORY_VALUES] }),
        },
        optionalProperties: ["title", "description", "dueDate", "priority", "status", "category"],
      }),
    },
    destructive: false,
    describe: (args, ctx) => `Aktualizacja zadania: ${taskTitle(ctx, asString(args.taskId) ?? "")}`,
    execute: async (args, ctx) => {
      const taskId = requireString(args, "taskId");
      if (!ctx.tasks().some((t) => t.id === taskId)) {
        throw new Error(`No task with ID "${taskId}". Call list_tasks first.`);
      }
      const patch: TaskPatch = {};
      const title = asString(args.title);
      const description = asString(args.description);
      const dueDate = normalizeDate(args.dueDate);
      const priority = asEnum(args.priority, PRIORITY_VALUES);
      const status = asEnum(args.status, STATUS_VALUES);
      const category = asEnum(args.category, CATEGORY_VALUES);
      if (title !== undefined) patch.title = title;
      if (description !== undefined) patch.description = description;
      if (dueDate !== undefined) patch.dueDate = dueDate;
      if (priority !== undefined) patch.priority = priority;
      if (category !== undefined) patch.category = category;
      if (status !== undefined) {
        patch.status = status;
        patch.completedAt = status === "done" ? new Date().toISOString() : null;
      }
      if (Object.keys(patch).length === 0) {
        throw new Error("No fields to update were provided.");
      }
      await ctx.operations.tasks.updateTask(ctx.weddingId, taskId, patch);
      return { ok: true, taskId, updated: Object.keys(patch) };
    },
  },
  {
    declaration: {
      name: "delete_task",
      description:
        "Permanently delete a task. You MUST first call list_tasks to obtain the exact taskId.",
      parameters: Schema.object({
        properties: {
          taskId: Schema.string({ description: "Exact task ID from list_tasks." }),
        },
      }),
    },
    destructive: true,
    describe: (args, ctx) => `Usunięcie zadania: ${taskTitle(ctx, asString(args.taskId) ?? "")}`,
    execute: async (args, ctx) => {
      const taskId = requireString(args, "taskId");
      if (!ctx.tasks().some((t) => t.id === taskId)) {
        throw new Error(`No task with ID "${taskId}". Call list_tasks first.`);
      }
      await ctx.operations.tasks.deleteTask(ctx.weddingId, taskId);
      return { ok: true, taskId };
    },
  },
];
